import { Button, Dialog, DialogActions, Box, DialogContent, IconButton, DialogTitle, Typography, Tooltip, Switch, Accordion, AccordionSummary, AccordionDetails, Divider } from '@mui/material';
import { setSuccessfullModifyDialog } from '../../../redux/successfullModifyDialogSlice';
import { setRoleDetailsData } from '../../../redux/roleDetailsDataSlice';
import { CustomErrorLogger } from '../../../utils/CustomErrorLogger';
import { refreshAuthenticatedUser } from '../../../services/authenticatedUser';
import { useState, useCallback, useEffect, useMemo } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Iconify from '../../Iconify';
import axios from 'axios';
import '../../../css/roles/updateRoleCredentials.css';

export const UpdateRoleCredentials = ({ updateRoleCredentials, setUpdateRoleCredentials }) => {

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const roleDetailsData = useSelector((state) => state.RoleDetailsData);
    const authenticatedUser = useSelector((state) => state.AuthenticatedUser);

    const [selectedCredentialIds, setSelectedCredentialIds] = useState(new Set(roleDetailsData?.role_credential?.map(credential => credential.credential?.id)));
    const [allCredentials, setAllCredentials] = useState([]);
    const [errMsg, setErrMsg] = useState(null);


    const handleClose = useCallback(() => {
        setUpdateRoleCredentials(false);
        setErrMsg(null)
    }, [setUpdateRoleCredentials]);

    // Fetch credentials once on component mount
    useEffect(() => {
        const fetchCredentials = async () => {
            try {
                const response = await axios.get('credential');
                if (response.status === 200 || response.status === 201) {
                    setAllCredentials(response.data);
                }
            } catch (error) {
                console.error(error, 'Fetch Credentials Error in UpdateRoleCredentials');
                CustomErrorLogger(error, "Fetch Credentials Error in UpdateRoleCredentials");
            }
        };
        fetchCredentials();
    }, []);

    const handleSwitchChange = useCallback((credential, checked) => {

        setSelectedCredentialIds((prevIds) => {
            const newIds = new Set(prevIds);
            if (newIds.has(credential.id) && !checked) {
                newIds.delete(credential.id);
            } else {
                // Add the current credential and check its parents
                newIds.add(credential.id);
            }
            return newIds;
        });
    }, []);

    const updateCredentials = async () => {
        const credentialIds = Array.from(selectedCredentialIds);
        try {
            const response = await axios.patch(`role/role_credentials/${roleDetailsData.id}`, { credentialIds });
            if (response.status === 200 || response.status === 201) {
                dispatch(setSuccessfullModifyDialog({ open: true, type: "Modifier les autorisations du rôle" }));
                dispatch(setRoleDetailsData({ ...roleDetailsData, role_credential: response.data.role_credential }));
                if (roleDetailsData?.id && roleDetailsData.id === authenticatedUser?.user_role?.id) {
                    await refreshAuthenticatedUser(dispatch);
                }
                setUpdateRoleCredentials(false);
            }
        } catch (error) {
            console.error('Error updating role credentials:', error);
            CustomErrorLogger(error, "Update Role Credentials Error in UpdateRoleCredentials");
            setErrMsg('Error updating role credentials');
        }
    };

    // Function to generate tooltip content
    const generateTooltipContent = (credential) => {
        return (
            <Box>
                {credential?.description && (
                    <Typography variant="caption" color="textSecondary">{credential.description}</Typography>
                )}
            </Box>
        );
    };

    // Memoize credentials list for rendering
    const credentialsList = useMemo(() => {
        return allCredentials?.map((credentialGrp, index) => (
            <Accordion fullWidth key={index} sx={{ width: '100%', backgroundColor: 'transparent', border: 'none', boxShadow: 'none', '&:before': { display: 'none' } }}>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ margin: '0 0 10px 0', padding: 0, backgroundColor: '#ebeef4', borderRadius: '5px', display: 'flex', flexDirection: 'row-reverse', gap: '5px' }}
                >
                    <Tooltip title={credentialGrp.groupDescription} arrow>
                        <Typography variant="h6" component="div">{credentialGrp.groupName}</Typography>
                    </Tooltip>
                </AccordionSummary>
                <AccordionDetails sx={{ margin: 0, padding: 0 }}>
                    {credentialGrp?.credentials?.map(credential => (
                        <Box key={credential.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', my: 2 }}>
                            <Tooltip title={generateTooltipContent(credential)} arrow>
                                <Typography>{credential?.name}</Typography>
                            </Tooltip>
                            <Switch
                                checked={selectedCredentialIds.has(credential.id)}
                                onChange={(e) => handleSwitchChange(credential, e.target.checked)}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                        color: '#EA5E00', // Change the color of the switch when checked
                                    },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                        backgroundColor: '#EA5E00', // Change the color of the track when checked
                                    },
                                }}
                            />
                        </Box>
                    ))}
                </AccordionDetails>
            </Accordion>
        ));
    }, [allCredentials, handleSwitchChange, selectedCredentialIds]);


    return (
        <Dialog open={updateRoleCredentials} fullWidth>
            <IconButton onClick={handleClose} className='updateRoleCredentials_icon_button'>
                <Iconify icon={"material-symbols:close"} className='updateRoleCredentials_close_icon' />
            </IconButton>
            <DialogTitle className='updateRoleCredentials_dialog_title'>{t('Gérer les autorisations du rôle')}</DialogTitle>
            <Divider />
            <DialogContent className='updateRoleCredentials_dialog_content'>
                <Typography className='updateRoleCredentials_dialog_SubTitle'>{t(`Veuillez sélectionner les informations d'identification.`)}</Typography>
                {errMsg && <Typography className='updateRoleCredentials_attention_text'>{errMsg}</Typography>}
                {credentialsList}
            </DialogContent>

            <DialogActions className='updateRoleCredentials_dialog_actions'>
                <Button className='updateRoleCredentials_button_annuler' onClick={handleClose}>{t('Annuler')}</Button>
                <Button className='updateRoleCredentials_button_valider' variant="contained" onClick={updateCredentials} disabled={Array.from(selectedCredentialIds).length === 0}>{t('Valider')}</Button>
            </DialogActions>
        </Dialog>
    );
};